import { AddIntegrationChannelDrawer } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer';
import {
  useFetchConnectedIntegrations,
  useOnConnectNewIntegration,
} from '@modules/integrationsV2/hooks';
import { useGetConnectedIntegrationWorkspaces } from '@modules/integrationsV2/hooks/useGetConnectedIntegrationWorkspaces';
import { AddRounded } from '@mui/icons-material';
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  PopoverProps,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  IntegrationChannelViewFragment,
  IntegrationType,
  IntegrationViewFragment,
} from 'generated/graphql';
import { useState } from 'react';

interface SelectOrAddWorkspaceMenuProps {
  integrationChannel: IntegrationChannelViewFragment;
  integrationType: IntegrationType;
  anchorEl: HTMLButtonElement | HTMLElement | null;
  closeMenu: () => void;
  oAuthURL: string | undefined;
  popoverProps?: Partial<PopoverProps>;
  onSelect?: (integrationID: string) => void;
  shouldShowAddWorkspace?: boolean;
}

export const SelectOrAddWorkspaceMenu = ({
  integrationChannel,
  integrationType,
  anchorEl,
  closeMenu,
  oAuthURL,
  popoverProps,
  onSelect,
  shouldShowAddWorkspace = true,
}: SelectOrAddWorkspaceMenuProps) => {
  const [isAddChannelDrawerOpen, setIsAddChannelDrawerOpen] = useState(false);

  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationViewFragment>();

  const onConnect = useOnConnectNewIntegration({
    integrationChannel,
    oAuthURL,
  });

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const connectedWorkspaces = useGetConnectedIntegrationWorkspaces({
    integrationType,
    channelType: integrationChannel.type,
  });

  const selectWorkspace = (id: string) => {
    closeMenu();

    if (onSelect) {
      onSelect(id);
      return;
    }

    const selectedIntegration = connectedIntegrations.find(
      (integration) => integration.id === id,
    );
    setSelectedIntegration(selectedIntegration);
    setIsAddChannelDrawerOpen(true);
  };

  const addWorkspace = () => {
    closeMenu();
    onConnect(integrationType);
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <Menu
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          sx: {
            pb: 0.8,
          },
        }}
        {...popoverProps}
      >
        <MenuList disablePadding>
          <Box
            px={1}
            pb={0.5}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Typography variant="caption">Select Workspace</Typography>
          </Box>
          {connectedWorkspaces.map((workspace) => {
            return (
              <MenuItem
                key={workspace.id}
                onClick={() => selectWorkspace(workspace.id)}
              >
                {workspace.name}
              </MenuItem>
            );
          })}
          <Box pb={0.8}>
            <Divider />
          </Box>
          {shouldShowAddWorkspace && (
            <MenuItem onClick={addWorkspace}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddRounded color="primary" fontSize="small" />
                <Typography color="primary">Add Workspace</Typography>
              </Stack>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {selectedIntegration && (
        <AddIntegrationChannelDrawer
          isOpen={isAddChannelDrawerOpen}
          onDrawerClose={() => setIsAddChannelDrawerOpen(false)}
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          integrationID={selectedIntegration.id}
          integrationData={selectedIntegration.data}
          channelDescriptions={
            selectedIntegration.description?.descriptionMap ?? []
          }
        />
      )}
    </>
  );
};
